<template>
  <div>
    <table class="mb-2.5 w-full table-auto border-collapse">
      <thead>
        <tr
          class="border-b-2 border-p-400"
          :class="!hasFrequency ? 'mt-4' : ''"
        >
          <th
            data-testid="more-info-desktop"
            class="mb-2 w-[410px] max-w-[410px]"
            :style="labelClassColor"
          >
            <div
              class="flex w-full select-none items-center gap-x-2 font-semibold"
            >
              {{ label }}
              <Icon
                v-if="isPriceDailyIndicator"
                ref="priceDailyIndicator"
                name="exclamation-square"
                class="hover:text-p-500"
              />
              <Icon v-if="showLogo" name="price-highlighted-logo" />
            </div>
          </th>
          <th
            v-for="column in columns"
            :key="column.key"
            class="w-[150px] max-w-[150px] bg-s-100 py-2 text-sm font-semibold text-surface-em-high lg:text-lg"
            :class="getClassesHeader(column)"
          >
            {{ column.header }}
          </th>
          <th
            class="w-16 text-sm font-semibold text-surface-em-high lg:text-lg"
          ></th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(item, i) in data"
          :key="item.name + i"
          class="tabular-nums odd:bg-white even:bg-s-100"
        >
          <td>
            <div
              class="flex max-w-[410px] items-center gap-x-3 py-2 pl-6 text-sm lg:text-base"
              :style="labelClassColor"
            >
              <div
                v-if="showPills"
                class="grid h-3 w-6 shrink-0 grid-cols-5 overflow-hidden rounded-full border border-s-500"
                :style="{ background: item.pillColor || '#000' }"
                data-testid="data-series-pill"
              >
                <div :style="{ background: item.pillColor }"></div>
                <div
                  :style="{
                    background: item.dotted ? 'white' : item.pillColor,
                  }"
                ></div>
                <div :style="{ background: item.pillColor }"></div>
                <div
                  :style="{
                    background: item.dotted ? 'white' : item.pillColor,
                  }"
                ></div>
                <div :style="{ background: item.pillColor }"></div>
              </div>
              {{ item.name }}
            </div>
          </td>
          <td v-for="column in columns" :key="column.key">
            <div class="text-xs font-semibold lg:text-base">
              <SummaryTableSectionItemNewPortal
                :authorized="authorized"
                :type="column.type"
                :item="getItemValueByKey(item, column.key)"
                :initial-fields="initialFields"
              />
            </div>
          </td>
          <td v-if="item.isPublic">
            <div class="flex items-center justify-center">
              <TooltipShare
                :twitter-link="
                  getTwitterLink(
                    label,
                    item.name,
                    frequency,
                    item.today || '--',
                    item.latestChange || '--',
                    item.yearOnYear || '--',
                    item.yearToDate || '--',
                  )
                "
                :linkedin-shareable-title="`${label} Index`"
                :linkedin-link="getRouteToShare"
                :link="getRouteToShare"
                class="text-black"
              />
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import tippy from 'tippy.js'
import Icon from '~/components/common/Icon.vue'
import SummaryTableSectionItemNewPortal from '~/components/price-assessments/SummaryTableSectionItemNewPortal.vue'
import TooltipShare from '~/components/TooltipShare.vue'
import { tooltipContent } from '~/constants/dailyIndicator'
import { getClassesColor } from '~/utils/get-classes-color'
import type { TPriceDataItem } from '~/src/types/product'
import type { TRecord } from '~/src/types/common'
import {
  getMarketForRoute,
  getPageForRoute,
  isSpecialMarket,
} from '~/src/utils/markets-with-prices'

type TColumn = {
  key: string
  header: string
  type: string
  isFrequency?: boolean
}

const props = withDefaults(
  defineProps<{
    label: string
    classTextColor: string
    data: Array<TPriceDataItem>
    authorized?: boolean
    frequency: string
    category?: string
    subscription?: string | null
    showPills?: boolean
    isSustainable?: boolean
    initialFields?: TRecord[] | string[]
    isPriceDailyIndicator?: boolean
    columns?: Array<TColumn>
    slugRouteShare?: string
  }>(),
  {
    authorized: false,
    category: 'cobalt',
    subscription: null,
    showPills: false,
    isSustainable: false,
    initialFields: () => [],
    isPriceDailyIndicator: false,
    columns: () => [],
    slugRouteShare: null,
  },
)

const { $config } = useNuxtApp()

const priceDailyIndicator = ref(null)

const showLogo = computed(
  () => props.classTextColor === 'industry-leading' || props.isSustainable,
)

const hasFrequency = computed(() => props.frequency !== '')

const getRouteToShare = computed(() => {
  const market = getMarketForRoute(props.category)
  const page = getPageForRoute(props.category)
  const slugPage = isSpecialMarket(props.category) ? `${page}-prices` : 'prices'
  const path = $config.public.isProduction
    ? $config.public.app.productionUrl
    : window.location.origin
  const route = `${path}/${market}/${slugPage}`
  return props.slugRouteShare ? `${route}/${props.slugRouteShare}` : route
})

const labelClassColor = computed(() => {
  let colorStyle = `color: ${getClassesColor(props.classTextColor)}`
  if (props.isSustainable) {
    colorStyle = `color: ${getClassesColor('industry-leading')}`
  }
  return colorStyle
})

const getItemValueByKey = (item: any, key: string) => {
  return {
    data: item[key],
    unitMeasure: item?.unitMeasure,
    currency: item?.currency,
    isPublic: item?.isPublic,
  }
}

const getTwitterLink = (
  indexLabel: string,
  valueLabel: string,
  frequencyLabel: string,
  todayValue: number | string,
  latestChangeValue: number | string,
  yoyValue: number | string,
  ytdValue: number | string,
) => {
  const CURRENT = todayValue ? `Current: ${todayValue}` : ''
  const LATEST_CHANGE = latestChangeValue
    ? `${frequencyLabel}: ${latestChangeValue}`
    : ''
  const YOY = yoyValue ? `YOY: ${yoyValue}` : ''
  const YTD = yoyValue ? `YTD: ${ytdValue}` : ''
  const encodePost = encodeURIComponent(`${indexLabel}
${valueLabel}
====================
${CURRENT}
${LATEST_CHANGE}
${YOY}
${YTD}

Source: @Benchmarkmin
See more at ${getRouteToShare.value}
`)
  return `https://twitter.com/intent/tweet?text=${encodePost}`
}

const getClassesHeader = (item: object) => {
  const index = props.columns.findIndex((column) => item.key === column.key)
  const isLastItem = index === props.columns.length - 1

  return [
    index === 0 ? 'rounded-tl-xl' : '',
    isLastItem ? 'rounded-tr-xl' : '',
    item.isFrequency ? 'uppercase' : '',
  ].filter(Boolean)
}

const priceDailyIndicatorTooltip = () => {
  tippy(priceDailyIndicator.value?.$el, {
    content: tooltipContent,
    allowHTML: true,
    maxWidth: 440,
    arrow: true,
    placement: 'bottom',
  })
}

onMounted(() => {
  if (!props.isPriceDailyIndicator) return
  priceDailyIndicatorTooltip()
})
</script>
